import type { TvSnapshot } from './protocol';

interface GameResultOverlayProps {
  snapshot: TvSnapshot | null;
}

/**
 * A match-ending card for Line Four and Turf War from an app build that
 * does not publish the game-over summary. The HUD remains visible
 * underneath as the detailed scorecard; this card owns the winner reveal.
 * Rematch and exit are chosen on the phone (the mirrored menu is shown as
 * text, never as buttons).
 */
export default function GameResultOverlay({ snapshot }: GameResultOverlayProps) {
  const game = snapshot?.game;
  if (!game || (game.kind !== 'lineFour' && game.kind !== 'turfWar') || game.phase !== 'result') {
    return null;
  }
  // A phone that publishes the game-over summary (2026-09-30) gets the full
  // layout; this card is the fallback for an older phone.
  if (snapshot?.summary) return null;

  let kicker: string;
  let title: string;
  let detail: string;
  if (game.kind === 'lineFour') {
    kicker = 'FOUR IN A ROW';
    title = game.state.isDraw
      ? 'DRAW'
      : `${game.names[game.state.winnerIndex ?? 0]} WINS`;
    detail = `${game.state.attemptCount} PUTTS`;
  } else {
    const winnerIndex = game.state.winnerIndex ?? (game.score[1] > game.score[0] ? 1 : 0);
    kicker = game.state.suddenDeath ? 'SUDDEN DEATH' : 'MOST TURF CONTROLLED';
    title = `${game.names[winnerIndex]} WINS`;
    detail = `${game.score[0]} – ${game.score[1]} ZONES`;
  }

  const expectedMenuId = game.kind === 'lineFour'
    ? 'challenge.linefour.result'
    : 'challenge.turfwar.result';
  const menu = snapshot?.menu?.id === expectedMenuId ? snapshot.menu : null;

  return (
    <section className="game-result-overlay" aria-live="polite">
      <div className={`game-result-card game-result-${game.kind.toLowerCase()}`}>
        <p>{kicker}</p>
        <h2>{title}</h2>
        <strong>{detail}</strong>
        {menu ? (
          <div className="game-result-actions" aria-label="Choices on your device">
            {menu.options.map((option) => (
              <span
                key={option.id}
                className={`game-result-choice${option.emphasis === 'primary' ? ' primary' : ''}`}
              >
                {option.label}
              </span>
            ))}
          </div>
        ) : null}
        <small>Choose on your device.</small>
      </div>
    </section>
  );
}
